import Table, { Cell, HeadCell, Row, TBody, THead } from '@atlaskit/table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type RowData,
  type SortingState,
  useReactTable
} from '@tanstack/react-table';
import { type ReactNode, useId, useState } from 'react';

const EMPTY_SORTING: SortingState = [];

interface DataTableProps<TData extends RowData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  emptyMessage?: ReactNode;
  getRowId?: (originalRow: TData, index: number) => string;
  initialSorting?: SortingState;
  maxHeight?: string;
  footerCells?: ReactNode[];
}

export function DataTable<TData extends RowData>({
  data,
  columns,
  emptyMessage = 'No data found',
  getRowId,
  initialSorting = EMPTY_SORTING,
  maxHeight = '70vh',
  footerCells
}: DataTableProps<TData>) {
  const tableId = useId().replace(/:/g, '');
  const [sorting, setSorting] = useState<SortingState>(() => initialSorting);
  const hasFooter = Boolean(footerCells?.length);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const headerGroups = table.getHeaderGroups();
  const rowModel = table.getRowModel();
  const tableClassName = `scroll-table-${tableId}`;
  const footerRow = footerCells?.length ? (
    <Row>
      {footerCells.map((cell, i) => (
        <Cell key={`${tableId}-footer-${i}`}>{cell}</Cell>
      ))}
    </Row>
  ) : (
    <></>
  );
  const headerContent = (
    <>
      {headerGroups[0]?.headers.map((header) => (
        <HeadCell key={header.id}>
          {header.isPlaceholder ? (
            <></>
          ) : (
            <button
              type="button"
              onClick={
                header.column.getCanSort()
                  ? header.column.getToggleSortingHandler()
                  : undefined
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                cursor: header.column.getCanSort()
                  ? 'pointer'
                  : 'default'
              }}
            >
              {flexRender(
                header.column.columnDef.header,
                header.getContext()
              )}
              {header.column.getCanSort() ? (
                <span>
                  {header.column.getIsSorted() === 'desc'
                    ? '↓'
                    : header.column.getIsSorted() === 'asc'
                      ? '↑'
                      : '↕'}
                </span>
              ) : (
                <></>
              )}
            </button>
          )}
        </HeadCell>
      ))}
    </>
  );
  const bodyContent =
    rowModel.rows.length === 0 ? (
      <>
        <Row>
          <Cell colSpan={columns.length}>{emptyMessage}</Cell>
        </Row>
      </>
    ) : (
      <>
        {rowModel.rows.map((row) => (
          <Row key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <Cell key={cell.id}>
                {flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                )}
              </Cell>
            ))}
          </Row>
        ))}
      </>
    );

  return (
    <div
      className={`${tableClassName}${hasFooter ? ' has-footer' : ''}`}
      style={{
        borderRadius: '12px',
        maxHeight,
        overflow: 'auto' // 🔥 single scroll container
      }}
    >
      <style>
        {`
        .${tableClassName} {
          scrollbar-width: none;
          -ms-overflow-style: none;
          color: var(--app-text);
        }

        .${tableClassName}::-webkit-scrollbar {
          display: none;
        }

        .${tableClassName} table {
          border-collapse: collapse;
          width: max-content;
          min-width: 100%;
        }

        .${tableClassName} th,
        .${tableClassName} td {
          min-width: 180px;
          padding: 10px 12px;
          white-space: nowrap;
          text-align: right;
          /* border: 1px solid var(--app-border); */
          color: var(--app-text);
        }

        /* Sticky header */
        .${tableClassName} thead th {
          position: sticky;
          top: 0;
          background: var(--app-table-head);
          z-index: 2;
          text-align: right;
        }

        /* Sticky first column */
        .${tableClassName} th:first-child,
        .${tableClassName} td:first-child {
          position: sticky;
          left: 0;
          background: var(--app-surface);
          z-index: 3;
          text-align: left;
          min-width: 220px;
          box-shadow: var(--app-shadow-sticky);
        }

        /* Header first column higher */
        .${tableClassName} thead th:first-child {
          z-index: 4;
          background: var(--app-table-head);
        }

        /* Footer sticky */
        .${tableClassName}.has-footer tbody tr:last-child{
          position: sticky;
          bottom: 0;
          background: var(--app-table-head);
          color: var(--app-text);
          font-weight: 700;
          z-index: 2;
        }

         .${tableClassName}.has-footer tbody tr:last-child td:first-child {
           background: var(--app-table-head);
         }

        .${tableClassName} tbody tr:hover td {
          background: var(--app-surface-selected);
        }

        // .${tableClassName} tbody tr:hover td:first-child {
        //   background: var(--app-surface-selected);
        // }

        .${tableClassName} tbody tr:nth-child(odd) {
          background: var(--app-surface);
        }

        .${tableClassName} tbody tr:nth-child(even) {
          background: var(--app-surface-subtle);
        }

        .${tableClassName} tbody tr:nth-child(odd) td:first-child {
          background: var(--app-surface);
        }

        .${tableClassName} tbody tr:nth-child(even) td:first-child {
          background: var(--app-surface-subtle);
        }
        `}
      </style>

      <Table>
        <THead>{headerContent}</THead>

        <TBody>
          {bodyContent}
          {footerRow}
        </TBody>
      </Table>
    </div>
  );
}

export default DataTable;
